import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/primitives';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Sign in / sign up.
 *
 * This is the one form in the app that uses React Hook Form + Zod, because it is
 * the one that fits: independent fields, per-field validation, and no live
 * preview forcing a re-render on every keystroke anyway. (The composer
 * deliberately does not — see the note there.)
 */
const authSchema = z.object({
  email: z.string().min(1, 'Enter your email.').email('That doesn’t look like an email address.'),
  password: z.string().min(6, 'Passwords need at least 6 characters.'),
});

type AuthValues = z.infer<typeof authSchema>;

// Demo login seeded by `pnpm seed` (scripts/seed.ts). Keep these two in sync.
const DEMO = { email: 'demo@postpilot.app', password: 'demo123456' } as const;

export function AuthPage() {
  const user = useAuthStore((s) => s.user);
  const initializing = useAuthStore((s) => s.initializing);
  const signIn = useAuthStore((s) => s.signIn);
  const signUp = useAuthStore((s) => s.signUp);

  const [mode, setMode] = React.useState<'signin' | 'signup'>('signin');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    setValue,
  } = useForm<AuthValues>({
    resolver: zodResolver(authSchema),
    // Validate on blur, not on change: telling someone their email is invalid
    // while they are still halfway through typing it is hostile.
    mode: 'onBlur',
  });

  // Wait for the session check before deciding. Redirecting on `!user` while
  // `initializing` would bounce a signed-in user to this page on every refresh.
  if (initializing) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (values: AuthValues) => {
    try {
      if (mode === 'signin') {
        await signIn(values.email, values.password);
      } else {
        await signUp(values.email, values.password);
        toast.success('Account created', {
          description: 'You are signed in and ready to go.',
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Something went wrong.';
      // Attach it to the form rather than only firing a toast — a toast the user
      // dismissed leaves no trace of why the login failed.
      setError('root', { message });
    }
  };

  // One-click demo: fill the fields (so the user sees what they are) then sign
  // in with the seeded credentials. Skips the whole sign-up + email dance.
  const useDemo = async () => {
    setValue('email', DEMO.email);
    setValue('password', DEMO.password);
    try {
      await signIn(DEMO.email, DEMO.password);
    } catch {
      setError('root', {
        message: 'Demo login not ready — run `pnpm seed` to create it.',
      });
    }
  };

  return (
    <div className="bg-background flex min-h-dvh items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="bg-primary text-primary-foreground mb-4 flex size-11 items-center justify-center rounded-xl">
            <svg viewBox="0 0 24 24" className="size-5" fill="none" aria-hidden>
              <path
                d="M4 19L20 5M20 5L12 19L10 13L4 11L20 5Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h1 className="text-xl font-semibold tracking-tight">
            {mode === 'signin' ? 'Sign in to PostPilot' : 'Create your account'}
          </h1>
          <p className="text-muted-foreground mt-1.5 text-sm">
            {mode === 'signin'
              ? 'Manage every X account from one dashboard.'
              : 'Compose once, publish everywhere.'}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              aria-invalid={Boolean(errors.email)}
              {...register('email')}
            />
            {errors.email && (
              <p className="text-destructive text-xs" role="alert">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              // Tells the password manager whether to offer to save a new one.
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              placeholder="••••••••"
              aria-invalid={Boolean(errors.password)}
              {...register('password')}
            />
            {errors.password ? (
              <p className="text-destructive text-xs" role="alert">
                {errors.password.message}
              </p>
            ) : (
              <p className="text-muted-foreground text-xs">At least 6 characters.</p>
            )}
          </div>

          {errors.root && (
            <div
              className="border-destructive/25 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-xs"
              role="alert"
            >
              {errors.root.message}
            </div>
          )}

          <Button type="submit" className="w-full" loading={isSubmitting}>
            {mode === 'signin' ? 'Sign in' : 'Create account'}
          </Button>

          <div className="flex items-center gap-3">
            <span className="bg-border h-px flex-1" />
            <span className="text-muted-foreground text-xs">or</span>
            <span className="bg-border h-px flex-1" />
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={isSubmitting}
            onClick={useDemo}
          >
            Use demo credentials
          </Button>
          <p className="text-muted-foreground text-center text-xs">
            demo@postpilot.app · no sign-up needed
          </p>
        </form>

        <p className="text-muted-foreground mt-6 text-center text-sm">
          {mode === 'signin' ? 'No account yet?' : 'Already have an account?'}{' '}
          <button
            type="button"
            onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
            className="text-foreground font-medium underline-offset-4 hover:underline"
          >
            {mode === 'signin' ? 'Create one' : 'Sign in'}
          </button>
        </p>
      </motion.div>
    </div>
  );
}
